import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Box, Container, Typography, Chip, Paper, Divider, Button, Avatar } from "@mui/material";
import { AccessTime, CalendarToday, PersonOutline, ArrowBack, Share } from "@mui/icons-material";
import Homenavbar from "./Homenavbar";
import Homefooter from "./Homefooter";
import DemoRequestDialog from "./Components/DemoRequestDialog";
import { blogPosts } from "./blogData";

const BlogPost = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [demoDialogOpen, setDemoDialogOpen] = useState(false);
    const [activeLink, setActiveLink] = useState('Blog');
    const [post, setPost] = useState(null);

    useEffect(() => {
        window.scrollTo(0, 0);
        // Find the post by ID
        const foundPost = blogPosts.find(p => p.id === id);
        if (foundPost) {
            setPost(foundPost);
            // Update Meta Tags (Basic Implementation - for SPA handling usually requires Helmet)
            document.title = foundPost.metaTitle || foundPost.title;
            const metaDescription = document.querySelector('meta[name="description"]');
            if (metaDescription) {
                metaDescription.setAttribute("content", foundPost.excerpt || "Read more about this topic on the JobsnProfiles blog.");
            }
        } else {
            // Handle not found
            // navigate("/blog");
        }
    }, [id]);

    const handleDemoOpen = () => setDemoDialogOpen(true);
    const handleDemoClose = () => setDemoDialogOpen(false);

    if (!post) {
        return (
            <Box sx={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <Typography>Loading...</Typography>
            </Box>
        );
    }

    const renderContent = (content) => {
        return content.map((section, index) => {
            switch (section.type) {
                case "heading":
                    return (
                        <Typography key={index} variant="h4" sx={{
                            fontWeight: 700,
                            mt: 6,
                            mb: 3,
                            color: "#1E293B",
                            fontFamily: "'Poppins', sans-serif"
                        }}>
                            {section.text}
                        </Typography>
                    );
                case "subtitle":
                    return (
                        <Typography key={index} variant="h6" sx={{
                            fontWeight: 600,
                            mt: 4,
                            mb: 2,
                            color: "#334155",
                            fontFamily: "'Poppins', sans-serif"
                        }}>
                            {section.text}
                        </Typography>
                    );
                case "paragraph":
                    return (
                        <Typography key={index} paragraph sx={{
                            color: "#475569",
                            fontSize: "1.1rem",
                            lineHeight: 1.8,
                            mb: 3
                        }}>
                            {section.text}
                        </Typography>
                    );
                case "list":
                    return (
                        <Box key={index} component="ul" sx={{ pl: 2, mb: 3 }}>
                            {section.items.map((item, i) => (
                                <li key={i} style={{ marginBottom: "12px", color: "#475569", fontSize: "1.1rem", lineHeight: "1.6" }}>
                                    {item}
                                </li>
                            ))}
                        </Box>
                    );
                case "list_bold":
                    return (
                        <Box key={index} component="ul" sx={{ pl: 2, mb: 3 }}>
                            {section.items.map((item, i) => (
                                <li key={i} style={{ marginBottom: "12px", color: "#475569", fontSize: "1.1rem", lineHeight: "1.6" }}>
                                    <strong>{item.title}</strong><br />
                                    {item.desc}
                                </li>
                            ))}
                        </Box>
                    );
                default:
                    return null;
            }
        });
    };

    return (
        <Box sx={{ backgroundColor: "#F8FAFC", minHeight: "100vh" }}>
            <Homenavbar
                onRequestDemo={handleDemoOpen}
                activeLink={activeLink}
                setActiveLink={setActiveLink}
            />

            <Box sx={{ pt: 12, pb: 4 }}>
                <Container maxWidth="md">
                    <Button
                        startIcon={<ArrowBack />}
                        onClick={() => navigate("/blog")}
                        sx={{ mb: 4, color: "#64748B", textTransform: "none", "&:hover": { color: "#FF6B00", background: "transparent" } }}
                    >
                        Back to Blog
                    </Button>

                    <Chip label={post.category} sx={{ backgroundColor: "rgba(255,107,0,0.1)", color: "#FF6B00", fontWeight: 600, mb: 3 }} />

                    <Typography variant="h2" sx={{
                        fontWeight: 800,
                        color: "#0F172A",
                        fontSize: { xs: "2rem", md: "3rem" },
                        lineHeight: 1.3,
                        mb: 4,
                        fontFamily: "'Poppins', sans-serif"
                    }}>
                        {post.title}
                    </Typography>

                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 3, mb: 6, pb: 4, borderBottom: "1px solid #E2E8F0" }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                            <CalendarToday sx={{ fontSize: 20, color: "#94A3B8" }} />
                            <Box>
                                <Typography variant="caption" sx={{ color: "#64748B", fontWeight: 600 }}>{post.date}</Typography>
                            </Box>
                        </Box>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 3 }}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                <AccessTime sx={{ fontSize: 18, color: "#94A3B8" }} />
                                <Typography variant="body2" sx={{ color: "#64748B" }}>{post.readTime}</Typography>
                            </Box>
                            {/* <Button startIcon={<Share />} sx={{ color: "#64748B", textTransform: "none" }}>Share</Button> */}
                        </Box>
                    </Box>

                    {/* Content */}
                    <Box component="article">
                        {renderContent(post.content)}
                    </Box>

                    {/* Tags */}
                    {post.tags && (
                        <Box sx={{ mt: 8, pt: 4, borderTop: "1px solid #E2E8F0" }}>
                            <Typography variant="subtitle2" sx={{ color: "#94A3B8", mb: 2, fontWeight: 600 }}>TAGS</Typography>
                            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5 }}>
                                {post.tags.map(tag => (
                                    <Chip
                                        key={tag}
                                        label={tag}
                                        sx={{ borderRadius: "8px", backgroundColor: "#F1F5F9", color: "#475569", fontWeight: 500 }}
                                    />
                                ))}
                            </Box>
                        </Box>
                    )}

                </Container>
            </Box>

            <Homefooter onRequestDemo={handleDemoOpen} />
            <DemoRequestDialog open={demoDialogOpen} onClose={handleDemoClose} />
        </Box>
    );
};

export default BlogPost;
